import { learnerReturnPath } from 'modules/auth/return-path';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AccountTypePicker, AuthSplitPanel } from 'components/auth';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import { useCompleteExternalAccountType } from 'modules/auth';
import { IS_IO } from 'lib/site';

const AccountType: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const returnTo = learnerReturnPath(new URLSearchParams(useLocation().search).get('returnTo'));
  const { toast } = useToast();
  const complete = useCompleteExternalAccountType();
  const [accountType, setAccountType] = React.useState<'STAFF' | 'CANDIDATE'>('CANDIDATE');

  const submit = async () => {
    try {
      await complete.mutateAsync({ accountType });
      navigate(returnTo ? `/onboarding?returnTo=${encodeURIComponent(returnTo)}` : '/onboarding', { replace: true });
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast.error(t('error'), { description: message || t('auth.could_not_create_account') });
    }
  };

  // examing.uz is learners only: skip the picker and finish as a candidate.
  const autoSubmitted = React.useRef(false);
  React.useEffect(() => {
    if (IS_IO || autoSubmitted.current) return;
    autoSubmitted.current = true;
    void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthSplitPanel
      title={t('create_your_account')}
      subtitle={t('auth.account_type_question')}
    >
      <div className="space-y-6">
        {IS_IO && <AccountTypePicker value={accountType} onChange={setAccountType} />}
        <Button type="button" className="w-full" size="lg" disabled={complete.isPending} onClick={submit}>
          {complete.isPending ? t('auth.signing_in') : t('continue')}
          {!complete.isPending && <ArrowRight className="ml-2 h-4 w-4" aria-hidden />}
        </Button>
      </div>
    </AuthSplitPanel>
  );
};

export default AccountType;
